import React, { useState, useEffect, useCallback } from 'react';
import { orderService, productService, extractErrorDetails } from '../services/api';
import BarcodeScanner from './BarcodeScanner';
import ErrorDisplay from './ErrorDisplay';

// Productos creados desde el alta rápida (sin código de barras real) quedan con
// codigoBarras = "SB-<id>" (ver ProductService.createProductWithoutBarcode)
const isWithoutBarcode = (product) => !!product.codigoBarras && product.codigoBarras.startsWith('SB-');

function CreateOrder({ onOrderCreated, existingOrder }) {
  const [descripcion, setDescripcion] = useState(existingOrder?.descripcion || '');
  const [items, setItems] = useState(
    (existingOrder?.items || []).map(item => ({
      productId: item.productId,
      codigoBarras: item.codigoBarras,
      nombreProducto: item.nombreProducto,
      cantidad: item.cantidad
    }))
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [descQuery, setDescQuery] = useState('');
  const [descResults, setDescResults] = useState([]);
  const [quickFormOpen, setQuickFormOpen] = useState(false);
  const [quickNombre, setQuickNombre] = useState('');
  const [quickDescripcion, setQuickDescripcion] = useState('');
  const [quickCreating, setQuickCreating] = useState(false);
  const [quickError, setQuickError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState('');
  const [errorTrace, setErrorTrace] = useState(null);
  const [success, setSuccess] = useState('');

  // Load all active products on mount
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoadingProducts(true);
        const response = await productService.getAllActiveProducts();
        if (response.success) {
          setAllProducts(response.data || []);
        }
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    loadProducts();
  }, []);

  // Local search filter - filters from preloaded products
  const handleSearch = useCallback((query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    const filtered = allProducts.filter(product => 
      product.nombre.toLowerCase().includes(lowerQuery) ||
      product.codigoBarras.toLowerCase().includes(lowerQuery)
    );
    setSearchResults(filtered.slice(0, 10)); // Limit to 10 results
  }, [allProducts]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchQuery);
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, handleSearch]);

  // Search by description/name for products without a known barcode
  const handleDescSearch = useCallback((query) => {
    if (!query || query.length < 2) {
      setDescResults([]);
      return;
    }

    const lowerQuery = query.toLowerCase();
    const filtered = allProducts.filter(product =>
      isWithoutBarcode(product) &&
      (product.nombre.toLowerCase().includes(lowerQuery) ||
        (product.descripcion && product.descripcion.toLowerCase().includes(lowerQuery)))
    );
    setDescResults(filtered.slice(0, 10));
  }, [allProducts]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleDescSearch(descQuery);
    }, 150);
    return () => clearTimeout(timeoutId);
  }, [descQuery, handleDescSearch]);

  const openQuickCreateForm = () => {
    setQuickNombre(descQuery);
    setQuickDescripcion('');
    setQuickError('');
    setQuickFormOpen(true);
  };

  const cancelQuickCreate = () => {
    setQuickFormOpen(false);
    setQuickNombre('');
    setQuickDescripcion('');
    setQuickError('');
  };

  const handleQuickCreate = async (e) => {
    e.preventDefault();
    setQuickError('');

    if (!quickNombre || !quickNombre.trim()) {
      setQuickError('El nombre es requerido');
      return;
    }

    setQuickCreating(true);
    try {
      const response = await productService.quickCreateProduct(quickNombre.trim(), quickDescripcion.trim());
      if (response.success && response.data) {
        setAllProducts(prev => [...prev, response.data]);
        addProductToOrder(response.data);
        setDescQuery('');
        setDescResults([]);
        cancelQuickCreate();
      } else {
        setQuickError(response.message || 'Error al crear el producto');
      }
    } catch (err) {
      const { message } = extractErrorDetails(err, 'Error al crear el producto');
      setQuickError(message);
    } finally {
      setQuickCreating(false);
    }
  };

  const handleScan = async (barcode) => {
    setShowScanner(false);
    setError('');
    setErrorTrace(null);
    try {
      const response = await productService.getProductByBarcode(barcode);
      if (response.success && response.data) {
        addProductToOrder(response.data);
      } else {
        setError(response.message || 'Producto no encontrado con ese código de barras');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Producto no encontrado con ese código de barras');
      setError(message);
      setErrorTrace(trace);
    }
  };

  const addProductToOrder = (product) => {
    const existingIndex = items.findIndex(item => item.productId === product.id);
    
    if (existingIndex >= 0) {
      // Increment quantity if already exists
      const newItems = [...items];
      newItems[existingIndex].cantidad += 1;
      setItems(newItems);
    } else {
      // Add new item
      setItems([...items, {
        productId: product.id,
        codigoBarras: product.codigoBarras,
        nombreProducto: product.nombre,
        cantidad: 1
      }]);
    }
    
    setSearchQuery('');
    setSearchResults([]);
    setError('');
  };

  const updateItemQuantity = (index, cantidad) => {
    const newItems = [...items];
    // Allow empty string for editing, will be validated on blur
    if (cantidad === '') {
      newItems[index].cantidad = '';
    } else {
      const numValue = parseInt(cantidad);
      newItems[index].cantidad = isNaN(numValue) ? 1 : Math.max(1, numValue);
    }
    setItems(newItems);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrorTrace(null);
    setSuccess('');

    if (items.length === 0) {
      setError('Debe agregar al menos un producto a la orden');
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        descripcion,
        items: items.map(item => ({
          productId: item.productId,
          cantidad: item.cantidad
        }))
      };

      const response = existingOrder
        ? await orderService.updateOrder(existingOrder.id, orderData)
        : await orderService.createOrder(orderData);

      if (response.success) {
        setSuccess(existingOrder ? 'Borrador actualizado exitosamente' : 'Borrador guardado exitosamente');
        if (!existingOrder) {
          setDescripcion('');
          setItems([]);
        }
        if (onOrderCreated) {
          onOrderCreated(response.data);
        }
      } else {
        setError(response.message || 'Error al guardar el borrador');
        setErrorTrace(response.errorTrace || null);
      }
    } catch (err) {
      const { message, errorTrace: trace } = extractErrorDetails(err, 'Error al guardar el borrador');
      setError(message);
      setErrorTrace(trace);
    } finally {
      setLoading(false);
    }
  };

  const clearForm = () => {
    setDescripcion('');
    setItems([]);
    setSearchQuery('');
    setSearchResults([]);
    setError('');
    setErrorTrace(null);
    setSuccess('');
  };

  return (
    <div className="create-order-container">
      {showScanner && (
        <BarcodeScanner
          onScan={handleScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      <div className="create-order-layout">
        {/* Product Search Section */}
        <div className="product-search-panel">
          <h3>Buscar Productos</h3>
          
          {loadingProducts ? (
            <p className="loading-text">Cargando productos...</p>
          ) : (
            <>
              <div className="search-input-container">
                <div className="input-with-button">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar por código o nombre..."
                    className="search-input"
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowScanner(true)}
                    className="btn btn-icon"
                    title="Escanear código de barras"
                  >
                    📷
                  </button>
                </div>
              </div>

              {searchResults.length > 0 && (
                <div className="product-results">
                  <h4>Resultados ({searchResults.length})</h4>
                  <ul className="product-list">
                    {searchResults.map(product => (
                      <li 
                        key={product.id}
                        onClick={() => addProductToOrder(product)}
                        className="product-item"
                      >
                        <span className="product-code">{product.codigoBarras}</span>
                        <span className="product-name">{product.nombre}</span>
                        <button
                          type="button" 
                          className="btn btn-small btn-primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            addProductToOrder(product);
                          }}
                        >
                          + Agregar
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {searchQuery.length >= 2 && searchResults.length === 0 && (
                <p className="no-results">No se encontraron productos</p>
              )}

              {searchQuery.length === 0 && (
                <p className="search-hint">Escriba al menos 2 caracteres para buscar</p>
              )}

              {/* Search for products without a barcode, by description */}
              <div className="no-barcode-section">
                <h3>Producto sin código de barras</h3>
                <p className="search-hint">Busque por descripción para ver artículos similares</p>

                <div className="search-input-container">
                  <input
                    type="text"
                    value={descQuery}
                    onChange={(e) => setDescQuery(e.target.value)}
                    placeholder="Describa el producto..."
                    className="search-input"
                  />
                </div>

                {descResults.length > 0 && (
                  <div className="product-results">
                    <h4>Productos similares ({descResults.length})</h4>
                    <ul className="product-list">
                      {descResults.map(product => (
                        <li
                          key={product.id}
                          onClick={() => addProductToOrder(product)}
                          className="product-item"
                        >
                          <span className="product-code">{product.codigoBarras}</span>
                          <span className="product-name">{product.nombre}</span>
                          <button
                            type="button"
                            className="btn btn-small btn-primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              addProductToOrder(product);
                            }}
                          >
                            + Agregar
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {descQuery.length >= 2 && descResults.length === 0 && !quickFormOpen && (
                  <p className="no-results">No se encontraron productos similares</p>
                )}

                {!quickFormOpen ? (
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={openQuickCreateForm}
                  >
                    + No lo encuentro, crear producto nuevo
                  </button>
                ) : (
                  <form onSubmit={handleQuickCreate} className="quick-create-form">
                    <div className="input-group">
                      <label htmlFor="quickNombre">Nombre del producto</label>
                      <input
                        id="quickNombre"
                        type="text"
                        value={quickNombre}
                        onChange={(e) => setQuickNombre(e.target.value)}
                        placeholder="Nombre del producto"
                        required
                      />
                    </div>
                    <div className="input-group">
                      <label htmlFor="quickDescripcion">Descripción (opcional)</label>
                      <input
                        id="quickDescripcion"
                        type="text"
                        value={quickDescripcion}
                        onChange={(e) => setQuickDescripcion(e.target.value)}
                        placeholder="Detalle adicional"
                      />
                    </div>

                    {quickError && <p className="error-message">{quickError}</p>}

                    <div className="form-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={cancelQuickCreate}
                        disabled={quickCreating}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary btn-small"
                        disabled={quickCreating}
                      >
                        {quickCreating ? 'Creando...' : 'Crear y agregar a la orden'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </>
          )}
        </div>

        {/* Order Form Section */}
        <div className="order-form-panel">
          <h3>{existingOrder ? `Editando Borrador #${existingOrder.id}` : 'Orden de Productos'}</h3>
          
          <form onSubmit={handleSubmit} className="order-form">
            <div className="input-group">
              <label htmlFor="descripcion">Descripción de la Orden</label>
              <textarea
                id="descripcion"
                name="descripcion"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Descripción de la solicitud (ej: Pedido urgente, Reposición semanal, etc.)"
                rows="2"
                required
              />
            </div>

            {items.length > 0 ? (
              <div className="order-items">
                <div className="order-items-header">
                  <h4>Productos en la Orden ({items.length})</h4>
                  <span className="total-units">
                    Total: {items.reduce((sum, item) => sum + item.cantidad, 0)} unidades
                  </span>
                </div>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Producto</th>
                      <th>Cantidad</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td>{item.codigoBarras}</td>
                        <td>{item.nombreProducto}</td>
                        <td>
                          <input
                            type="number"
                            value={item.cantidad}
                            onChange={(e) => updateItemQuantity(index, e.target.value)}
                            onBlur={(e) => {
                              // Ensure valid quantity on blur
                              if (e.target.value === '' || parseInt(e.target.value) < 1) {
                                updateItemQuantity(index, '1');
                              }
                            }}
                            min="1"
                            className="quantity-input"
                          />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={() => removeItem(index)}
                            className="btn btn-small btn-danger"
                          >
                            ✕
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-cart">
                <p>No hay productos en la orden</p>
                <p className="hint">Use la búsqueda para agregar productos</p>
              </div>
            )}

            <ErrorDisplay error={error} errorTrace={errorTrace} />
            {success && <p className="success-message">{success}</p>}

            <div className="form-actions">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={clearForm}
                disabled={loading}
              >
                Limpiar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || items.length === 0}
              >
                {loading ? 'Guardando...' : 'Guardar Borrador'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default CreateOrder;
